import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { curSym } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, FilePen, Clock3, Search, Send, Pencil, PackageCheck } from "lucide-react";

// Écran Opérations → Devis (maquette validée).
// Statuts : Devis à préparer → Brouillon (sauvegardé, non envoyé) → En attente de décision (envoyé).
// « En attente de paiement » n'apparaît JAMAIS ici. Modification/renvoi = MÊME dossier, même référence.
const QUOTE_ST = {
  to_prepare: ["Devis à préparer", "bg-blue-100 text-blue-700"],
  draft: ["Brouillon", "bg-muted text-muted-foreground"],
  sent: ["En attente de décision", "bg-amber-100 text-amber-700"],
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

// Ligne « Déclaration » : colis · contenu · poids (déclaré par le client)
const declLine = (r) => {
  const parts = [];
  if (r.expected) parts.push(`${r.expected} colis`);
  if (r.goods_description) parts.push(r.goods_description);
  if (r.declared_weight_kg) parts.push(`${r.declared_weight_kg} kg`);
  return parts.join(" · ") || "—";
};

// Estimation affichée : montant du devis en cours sinon estimation client existante
const estOf = (r) => {
  const amt = r.quote?.amount ?? r.estimated_price ?? r.items_total;
  return amt != null ? `${amt} ${curSym(r.quote?.currency || r.currency)}` : "—";
};

export default function AdminQuotes({ rows, onReceive, onDone }) {
  const [q, setQ] = useState("");
  const [destF, setDestF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [dlg, setDlg] = useState(null); // { row, mode: "edit" | "view" }

  const dests = useMemo(() => [...new Set(rows.map((r) => r.dest).filter(Boolean))], [rows]);
  const stOf = (r) => r.quote_status || "to_prepare";
  const filtered = rows.filter((r) => {
    const text = `${r.customer_name} ${r.client_code} ${r.reference}`.toLowerCase();
    if (q && !text.includes(q.toLowerCase())) return false;
    if (destF !== "all" && r.dest !== destF) return false;
    if (statusF !== "all" && stOf(r) !== statusF) return false;
    return true;
  });
  const count = (s) => rows.filter((r) => stOf(r) === s).length;

  return (
    <div className="mt-4 space-y-4" data-testid="quotes-screen">
      {/* Cartes statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          ["quotes-stat-new", FileText, count("to_prepare"), "nouveaux", "text-primary"],
          ["quotes-stat-draft", FilePen, count("draft"), "brouillons", "text-muted-foreground"],
          ["quotes-stat-sent", Clock3, count("sent"), "en attente de décision", "text-accent"],
        ].map(([tid, Icon, n, lbl, color]) => (
          <div key={tid} data-testid={tid} className="border border-border bg-card p-4 flex items-center gap-3">
            <div className={`h-10 w-10 flex items-center justify-center bg-secondary ${color}`}><Icon className="h-5 w-5" /></div>
            <div><div className={`font-heading font-extrabold text-2xl ${color}`}>{n}</div>
              <div className="text-xs text-muted-foreground">{lbl}</div></div>
          </div>
        ))}
      </div>

      {/* Bandeau info */}
      <p data-testid="quotes-info" className="text-sm border border-blue-200 bg-blue-50 text-blue-800 px-3 py-2">
        Le devis est envoyé individuellement au client concerné. Tant qu&rsquo;il n&rsquo;a pas accepté,
        vous pouvez le modifier ou le renvoyer — toujours sur le même dossier, sans nouvelle référence.
      </p>

      {/* Recherche + filtres */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input data-testid="quotes-search" className="rounded-sm h-9 pl-8" placeholder="Rechercher un client, une référence…"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={destF} onValueChange={setDestF}>
          <SelectTrigger data-testid="quotes-filter-dest" className="rounded-sm h-9 sm:w-48"><SelectValue placeholder="Destination" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes destinations</SelectItem>
            {dests.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="quotes-filter-status" className="rounded-sm h-9 sm:w-52"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(QUOTE_ST).map(([v, [l]]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tableau des devis */}
      <div className="border border-border bg-card overflow-x-auto" data-testid="quotes-table">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2">Client / Référence</th>
              <th className="px-3 py-2">Déclaration</th>
              <th className="px-3 py-2">Estimation</th>
              <th className="px-3 py-2">Statut</th>
              <th className="px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const st = stOf(r);
              const [lbl, cls] = QUOTE_ST[st] || QUOTE_ST.to_prepare;
              return (
                <tr key={r.id} data-testid={`quote-row-${r.id}`} className="border-b border-border last:border-0 align-middle">
                  <td className="px-3 py-2.5">
                    <div className="font-semibold">{r.customer_name || "—"}</div>
                    <div className="font-mono text-xs text-muted-foreground">{r.reference}</div>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{declLine(r)}
                    {r.dest && <div className="text-xs">→ {r.dest}</div>}</td>
                  <td className="px-3 py-2.5 font-bold">{estOf(r)}</td>
                  <td className="px-3 py-2.5"><Badge data-testid={`quote-status-${r.id}`} className={`rounded-sm ${cls}`}>{lbl}</Badge></td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {st === "to_prepare" && (
                        <Button size="sm" data-testid={`quote-prepare-${r.id}`} className="rounded-sm h-8 bg-accent hover:bg-accent/90 text-white"
                          onClick={() => setDlg({ row: r, mode: "edit" })}>Préparer le devis</Button>
                      )}
                      {st === "draft" && (
                        <Button size="sm" variant="outline" data-testid={`quote-continue-${r.id}`} className="rounded-sm h-8"
                          onClick={() => setDlg({ row: r, mode: "edit" })}>Continuer</Button>
                      )}
                      {st === "sent" && (
                        <Button size="sm" variant="outline" data-testid={`quote-view-${r.id}`} className="rounded-sm h-8"
                          onClick={() => setDlg({ row: r, mode: "view" })}>Voir le devis</Button>
                      )}
                      {onReceive && (
                        <Button size="sm" variant="ghost" data-testid={`ops-receive-${r.id}`} title="Réceptionner (le physique reste toujours possible)"
                          className="rounded-sm h-8 text-xs text-muted-foreground gap-1" onClick={() => onReceive(r)}>
                          <PackageCheck className="h-3.5 w-3.5" /> Réceptionner
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Aucun devis à traiter.</p>}
      </div>
      <p className="text-xs text-muted-foreground">Affichage de {filtered.length ? 1 : 0} à {filtered.length} sur {rows.length} devis</p>

      {dlg && <QuoteDialog row={dlg.row} mode={dlg.mode} onClose={() => setDlg(null)} onDone={onDone} />}
    </div>
  );
}

// Formulaire / détail de devis — préremplit tout ce que le client a déclaré.
function QuoteDialog({ row, mode: initialMode, onClose, onDone }) {
  const [mode, setMode] = useState(initialMode);
  const [amount, setAmount] = useState(row.quote?.amount ?? row.estimated_price ?? row.items_total ?? "");
  const [busy, setBusy] = useState(false);
  const cur = curSym(row.quote?.currency || row.currency);
  const submit = async (status) => {
    if (amount === "" || Number(amount) <= 0) { toast.error("Saisissez le montant du devis"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/ops/shipments/${row.id}/quote`, { status, amount: Number(amount) });
      toast.success(status === "draft"
        ? `Brouillon enregistré — ${row.reference} (rien n'a été envoyé au client)`
        : `Devis ${data.quote?.sent_count > 1 ? "renvoyé" : "envoyé"} à ${row.customer_name || "au client"} — ${amount} ${cur}`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  const markAccepted = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/ops/shipments/${row.id}/quote`, { status: "accepted" });
      toast.success(`Devis ${row.reference} accepté — dossier déplacé dans À recevoir`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  const info = [
    ["Référence", row.reference],
    ["Client", `${row.customer_name || "—"}${row.client_code ? ` · ${row.client_code}` : ""}`],
    ["Contenu", row.goods_description || "—"],
    ["Quantité", row.expected ? `${row.expected} colis` : "—"],
    ["Poids estimé", row.declared_weight_kg ? `${row.declared_weight_kg} kg` : "—"],
    ...(row.declared_value != null ? [["Valeur déclarée", `${row.declared_value} ${cur}`]] : []),
    ["Origine", row.origin || row.source || "—"],
    ["Destination", row.dest || "—"],
    ["Mode", row.mode === "air" ? "Aérien" : row.mode === "sea" ? "Maritime" : row.mode || "—"],
    ...(row.estimated_price != null || row.items_total != null
      ? [["Estimation existante", `${row.estimated_price ?? row.items_total} ${cur}`]] : []),
  ];
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="quote-dialog" className="rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "view" ? "Devis envoyé" : "Préparer le devis"} — {row.reference}</DialogTitle>
          <DialogDescription>
            {mode === "view"
              ? "Tant que le client n'a pas accepté, vous pouvez modifier ou renvoyer ce devis (même dossier, même référence)."
              : "Informations déclarées par le client — complétez ou corrigez le montant du devis."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm border border-border bg-secondary/40 p-3" data-testid="quote-declared-info">
          {info.map(([l, v]) => (
            <React.Fragment key={l}>
              <span className="text-muted-foreground">{l}</span>
              <span className="font-semibold">{v}</span>
            </React.Fragment>
          ))}
          {row.declared_items?.length > 0 && (
            <>
              <span className="text-muted-foreground">Articles déclarés</span>
              <span>{row.declared_items.map((it) => `${it.qty || 1} × ${it.name}`).join(", ")}</span>
            </>
          )}
        </div>

        {mode === "view" ? (
          <div className="space-y-2" data-testid="quote-view-panel">
            <div className="border border-border bg-card p-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Montant proposé</div>
                <div data-testid="quote-view-amount" className="font-heading font-extrabold text-2xl text-accent">{row.quote?.amount} {cur}</div>
              </div>
              <div className="text-right">
                <Badge className="rounded-sm bg-amber-100 text-amber-700">En attente de décision</Badge>
                <div className="text-xs text-muted-foreground mt-1" data-testid="quote-sent-at">
                  Envoyé le {fmtDate(row.quote?.sent_at)}{(row.quote?.sent_count || 1) > 1 ? ` · ${row.quote.sent_count}ᵉ envoi` : ""}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" data-testid="quote-edit" disabled={busy} className="rounded-sm gap-1.5" onClick={() => setMode("edit")}>
                <Pencil className="h-4 w-4" /> Modifier le devis
              </Button>
              <Button data-testid="quote-resend" disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" onClick={() => submit("sent")}>
                <Send className="h-4 w-4" /> Renvoyer le devis
              </Button>
            </div>
            <Button variant="outline" size="sm" data-testid="quote-accepted" disabled={busy}
              className="w-full rounded-sm border-emerald-600 text-emerald-700" onClick={markAccepted}>
              Devis accepté (confirmé par le client) — déplacer dans À recevoir
            </Button>
          </div>
        ) : (
          <>
            <div>
              <Label className="text-xs">Montant du devis ({cur})</Label>
              <Input data-testid="quote-amount-input" type="number" min="0" step="0.01" className="rounded-sm h-10 font-bold text-lg"
                value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" data-testid="quote-save-draft" disabled={busy} className="rounded-sm" onClick={() => submit("draft")}>
                Enregistrer le brouillon
              </Button>
              <Button data-testid="quote-send" disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" onClick={() => submit("sent")}>
                <Send className="h-4 w-4" /> Envoyer le devis
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
